import React, { useEffect, useRef } from 'react'
import {
  Copy, Trash2, ArrowUp, ArrowDown,
  Lock, LockOpen, Bookmark, Code, Accessibility,
} from 'lucide-react'

/* ── Props ───────────────────────────────────────────── */

interface BlockContextMenuProps {
  x: number
  y: number
  blockId: string
  isLocked: boolean
  onClose: () => void
  onDuplicate: () => void
  onRemove: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onToggleLock: () => void
  onSaveAsTemplate?: () => void
  onViewCode?: () => void
  onCheckA11y?: () => void
}

/* ── Menu item definition ────────────────────────────── */

interface MenuItem {
  label: string
  icon: React.ReactNode
  shortcut?: string
  danger?: boolean
  disabled?: boolean
  action: () => void
}

/* ── Component ───────────────────────────────────────── */

export default function BlockContextMenu({
  x,
  y,
  blockId,
  isLocked,
  onClose,
  onDuplicate,
  onRemove,
  onMoveUp,
  onMoveDown,
  onToggleLock,
  onSaveAsTemplate,
  onViewCode,
  onCheckA11y,
}: BlockContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)

  // Close on outside click or escape
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) onClose()
    }
    document.addEventListener('keydown', handleEsc)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleEsc)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [onClose])

  // Menu sections
  const sections: (MenuItem | 'divider')[][] = [
    // Move section
    [
      { label: 'Move Up', icon: <ArrowUp size={13} />, shortcut: '⌘↑', disabled: isLocked, action: onMoveUp },
      { label: 'Move Down', icon: <ArrowDown size={13} />, shortcut: '⌘↓', disabled: isLocked, action: onMoveDown },
    ],
    // Edit section
    [
      { label: 'Duplicate', icon: <Copy size={13} />, shortcut: '⌘D', disabled: isLocked, action: onDuplicate },
      { label: isLocked ? 'Unlock' : 'Lock', icon: isLocked ? <LockOpen size={13} /> : <Lock size={13} />, shortcut: '⌘L', action: onToggleLock },
    ],
    // Tools section
    [
      ...(onSaveAsTemplate ? [{ label: 'Save as Template', icon: <Bookmark size={13} />, action: onSaveAsTemplate } as MenuItem] : []),
      ...(onViewCode ? [{ label: 'View Code', icon: <Code size={13} />, shortcut: '⌘I', action: onViewCode } as MenuItem] : []),
      ...(onCheckA11y ? [{ label: 'Check Accessibility', icon: <Accessibility size={13} />, action: onCheckA11y } as MenuItem] : []),
    ],
    // Danger section
    [
      { label: 'Delete', icon: <Trash2 size={13} />, shortcut: '⌫', danger: true, disabled: isLocked, action: onRemove },
    ],
  ].filter(section => section.length > 0)

  return (
    <div
      ref={menuRef}
      className="editor-context-menu"
      style={{ left: x, top: y }}
      data-testid={`context-menu-${blockId}`}
    >
      {sections.map((section, si) => (
        <React.Fragment key={si}>
          {si > 0 && <div className="editor-context-divider" />}
          {section.map((item) => {
            if (item === 'divider') return <div key={`div-${si}`} className="editor-context-divider" />
            const mi = item as MenuItem
            return (
              <button
                key={mi.label}
                className="editor-context-item"
                data-danger={mi.danger}
                disabled={mi.disabled}
                onClick={() => { mi.action(); onClose() }}
                style={mi.disabled ? { opacity: 0.35, pointerEvents: 'none' } : undefined}
              >
                {mi.icon}
                <span>{mi.label}</span>
                {mi.shortcut && <span className="shortcut">{mi.shortcut}</span>}
              </button>
            )
          })}
        </React.Fragment>
      ))}
    </div>
  )
}
